import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    Alert,
    Card,
    CardContent,
    Box,
    CircularProgress,
    FormControl,
    IconButton,
    MenuItem,
    Select,
    Tooltip,
    Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { TrendingUp } from "@mui/icons-material";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as ChartTooltip,
} from "recharts";
import {
    getROIConfig,
    getWorkstationCameras,
    getWorkstationOccupancyTimeline,
} from "../../utils/api";
const WORKSTATION_TYPE = "WORKSTATION";
const TIME_ZONE = "Asia/Kolkata";
const normalizeCameras = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.cameras)) return response.cameras;
    if (Array.isArray(response?.items)) return response.items;
    if (Array.isArray(response?.data)) return response.data;
    return [];
};
const getCameraId = (camera) =>
    String(camera?.camera_id ?? camera?.id ?? "").trim();
const getWorkspaceName = (camera, index) =>
    camera?.label ||
    camera?.name ||
    camera?.camera_name ||
    `Workspace ${index + 1}`;
const getLaneRois = (response) => {
    const rois = response?.lane_rois ?? response?.data?.lane_rois;
    return rois &&
        typeof rois === "object" &&
        !Array.isArray(rois)
        ? rois
        : {};
};
const getResponseItems = (response) => {
    if (Array.isArray(response?.items)) return response.items;
    if (Array.isArray(response?.data?.items)) {
        return response.data.items;
    }
    if (Array.isArray(response)) return response;
    return [];
};
// Format the current date in Asia/Kolkata without relying on UTC dates.
const getLocalDateString = () => {
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: TIME_ZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(new Date());
    const values = Object.fromEntries(
        parts.map(({ type, value }) => [type, value])
    );
    return `${values.year}-${values.month}-${values.day}`;
};
const getLocalHour = () => {
    const hour = new Intl.DateTimeFormat("en-GB", {
        timeZone: TIME_ZONE,
        hour: "2-digit",
        hourCycle: "h23",
    }).format(new Date());
    return Number(hour);
};
const getTimestamp = (value) => {
    if (!value) return null;
    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
};
const getEventTimestamp = (event) =>
    getTimestamp(
        event?.ts ??
        event?.timestamp ??
        event?.occurred_at ??
        event?.event_time ??
        event?.created_at ??
        event?.at
    );
const getEventType = (event) =>
    String(
        event?.event ??
        event?.event_type ??
        event?.type ??
        event?.action ??
        ""
    )
        .trim()
        .toLowerCase();
// Supports the session format returned by the occupancy timeline API.
const getSessionIntervals = (timeline, now) => {
    const intervals = [];
    const sessions = Array.isArray(timeline?.sessions)
        ? timeline.sessions
        : [];
    sessions.forEach((session) => {
        const start = getTimestamp(
            session?.started_at ??
            session?.start_time ??
            session?.check_in
        );
        const end = getTimestamp(
            session?.ended_at ??
            session?.end_time ??
            session?.check_out
        );
        if (start === null) return;
        // An open session remains occupied until an exit is recorded.
        const isClosed = String(session?.status ?? "").toLowerCase() === "closed";
        const isOpen = end === null && !isClosed;
        const effectiveEnd =
            end !== null
                ? end
                : isClosed
                    ? getTimestamp(session?.last_seen_at) ?? start
                    : now;
        if (effectiveEnd >= start) {
            intervals.push({ start, end: effectiveEnd, open: isOpen });
        }
    });
    if (intervals.length > 0) return intervals;
    // Fallback for APIs returning check_in/check_out events instead
    // of sessions. Ignore heartbeat events.
    const days = Array.isArray(timeline?.days) ? timeline.days : [];
    const events = [
        ...(Array.isArray(timeline?.events) ? timeline.events : []),
        ...days.flatMap((day) =>
            Array.isArray(day?.events) ? day.events : []
        ),
    ];
    const uniqueEvents = Array.from(
        new Map(
            events.map((event, index) => [
                `${getEventType(event)}-${getEventTimestamp(event)}-${index}`,
                event,
            ])
        ).values()
    )
        .map((event) => ({
            type: getEventType(event),
            timestamp: getEventTimestamp(event),
        }))
        .filter((event) => event.timestamp !== null)
        .sort((a, b) => a.timestamp - b.timestamp);
    let occupiedSince = null;
    uniqueEvents.forEach((event) => {
        if (
            ["enter", "check_in", "checkin", "in"].includes(event.type)
        ) {
            if (occupiedSince === null) {
                occupiedSince = event.timestamp;
            }
        } else if (
            ["exit", "check_out", "checkout", "out"].includes(event.type)
        ) {
            if (occupiedSince !== null) {
                intervals.push({
                    start: occupiedSince,
                    end: event.timestamp,
                });
                occupiedSince = null;
            }
        }
    });
    // If no exit has arrived, the workstation remains occupied.
    if (occupiedSince !== null) {
        intervals.push({ start: occupiedSince, end: now, open: true });
    }
    return intervals;
};
// Count occupancy at one exact timestamp using session intervals.
const countOccupiedAt = (workstations, timestamp) =>
    workstations.filter((workstation) =>
        workstation.intervals.some(({ start, end, open }) =>
            start <= timestamp && (open || end > timestamp)
        )
    ).length;

// Each point represents occupancy at the exact start of its labelled hour.
const buildHourlyData = (workstations, now) => {
    const currentDate = getLocalDateString();
    const todayStart = new Date(
        `${currentDate}T00:00:00+05:30`
    ).getTime();
    const startHour = 9;  // 9 AM
    const endHour = 21;   // 9 PM
    return Array.from(
        { length: endHour - startHour + 1 },
        (_, index) => {
            const hour = startHour + index;
            const hourStart = todayStart + hour * 60 * 60 * 1000;
            const isFuture = hourStart > now;
const isCurrentHour = hour === getLocalHour();

const sampleTime = isCurrentHour
    ? now
    : hourStart;
            const occupied = isFuture ? null : countOccupiedAt(workstations, sampleTime);
            const label = new Date(hourStart).toLocaleTimeString(
                "en-IN",
                {
                    timeZone: TIME_ZONE,
                    hour: "numeric",
                    hour12: true,
                }
            );
            return {
                hour,
                time: label,
                occupied,
                isFuture,
            };
        }
    );
};
export default function WorkstationOccupancyChart() {
    const [selectedWorkspace, setSelectedWorkspace] = useState("all");
    const [workspaces, setWorkspaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");
    const [lastUpdated, setLastUpdated] = useState(null);
    const [now, setNow] = useState(Date.now());
    const [selectedPoint, setSelectedPoint] = useState(null);
    const isFetchingRef = useRef(false);
    const lastHourKeyRef = useRef(`${getLocalDateString()}-${getLocalHour()}`);

    const loadOccupancy = useCallback(async (initial = false) => {
        // Prevent overlapping requests when polling and the hour-boundary
        // refresh happen close together.
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;

        if (initial) {
            setLoading(true);
        } else {
            setRefreshing(true);
        }
        setError("");
        try {
            const now = Date.now();
            const date = getLocalDateString();
            const cameraResponse = await getWorkstationCameras();
            const cameras = normalizeCameras(cameraResponse);
            const results = await Promise.all(
                cameras.map(async (camera, index) => {
                    const cameraId = getCameraId(camera);
                    if (!cameraId) return null;
                    const roiResult = await getROIConfig(
                        WORKSTATION_TYPE,
                        { cameraId }
                    ).catch(() => null);
                    const laneRois = getLaneRois(roiResult);
                    const workstationIds = Object.keys(laneRois);
                    const workstationResults = await Promise.all(
                        workstationIds.map(async (workstationId) => {
                            try {
                                const timeline =
                                    await getWorkstationOccupancyTimeline({
                                        cameraId,
                                        workstationId,
                                        date,
                                    });
                                return {
                                    id: String(workstationId),
                                    intervals: getSessionIntervals(timeline, now),
                                };
                            } catch (err) {
                                console.error(
                                    `Failed to fetch timeline for ${cameraId}/${workstationId}:`,
                                    err
                                );
                                return {
                                    id: String(workstationId),
                                    intervals: [],
                                    failed: true,
                                };
                            }
                        })
                    );
                    return {
                        id: cameraId,
                        name: getWorkspaceName(camera, index),
                        workstations: workstationResults,
                    };
                })
            );
            const validWorkspaces = results.filter(Boolean);
            setWorkspaces(validWorkspaces);
            if (validWorkspaces.length === 0) {
                setError("No workspaces were returned by the camera API.");
            } else if (
                validWorkspaces.every(
                    (workspace) =>
                        workspace.workstations.length === 0
                )
            ) {
                setError(
                    "No workstation ROIs were found. Please check the ROI configuration."
                );
            } else if (
                validWorkspaces.some((workspace) =>
                    workspace.workstations.some((desk) => desk.failed)
                )
            ) {
                setError(
                    "Some workstation timelines could not be loaded. Their occupancy may be undercounted."
                );
            }
            setLastUpdated(new Date());
        } catch (err) {
            console.error("Failed to load hourly occupancy:", err);
            setError(
                err?.message ||
                "Unable to load workstation occupancy. Please retry."
            );
        } finally {
            isFetchingRef.current = false;
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    // Initial fetch and poll for new occupancy events every 30 seconds.
   useEffect(() => {
  loadOccupancy(true);

  const intervalId = setInterval(() => {
    loadOccupancy(false);
  }, 30 * 1000);

  return () => clearInterval(intervalId);
}, [loadOccupancy]);

    // Keep the current-time KPI fresh and fetch immediately when the hour
    // changes (for example, at 3:00 PM), rather than waiting for the poll.
    useEffect(() => {
        const clockId = setInterval(() => {
            const currentTime = Date.now();
            setNow(currentTime);
            const hourKey = `${getLocalDateString()}-${getLocalHour()}`;
            if (hourKey !== lastHourKeyRef.current) {
                lastHourKeyRef.current = hourKey;
                loadOccupancy(false);
            }
        }, 1000);
        return () => clearInterval(clockId);
    }, [loadOccupancy]);
    const selectedWorkspaces = useMemo(
        () =>
            selectedWorkspace === "all"
                ? workspaces
                : workspaces.filter(
                    (workspace) => workspace.id === selectedWorkspace
                ),
        [workspaces, selectedWorkspace]
    );
    const selectedWorkstations = useMemo(
        () =>
            selectedWorkspaces.flatMap((workspace) =>
                workspace.workstations.map((desk) => ({
                    ...desk,
                    workspaceId: workspace.id,
                    workspaceName: workspace.name,
                }))
            ),
        [selectedWorkspaces]
    );
    const chartData = useMemo(
        () => buildHourlyData(selectedWorkstations, now),
        [selectedWorkstations, now]
    );
    const selectedName =
        selectedWorkspace === "all"
            ? "All Workspaces"
            : workspaces.find(
                (workspace) => workspace.id === selectedWorkspace
            )?.name || "Workspace";
    const totalWorkstations = selectedWorkstations.length;
    const currentOccupied = countOccupiedAt(selectedWorkstations, now);
    return (
        <Card
            sx={{
                width: "100%",
                minWidth: 0,
                height: "100%",
                boxSizing: "border-box",
                background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                border: "1px solid rgba(64, 81, 138, 0.12)",
                borderRadius: 3,
                boxShadow: "0 10px 28px rgba(31, 45, 84, 0.12)",
                transition: "all 0.3s ease-in-out",
                "&:hover": { boxShadow: "0 12px 32px rgba(31, 45, 84, 0.16)" },
                overflow: "visible",
            }}
        >
            <CardContent
                sx={{
                    p: { xs: 1.5, sm: 2.25, md: 2.5 },
                    "&:last-child": { pb: { xs: 1.5, sm: 2.25, md: 2.5 } },
                    minWidth: 0,
                    boxSizing: "border-box",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: { xs: "flex-start", sm: "center" },
                        gap: 1.5,
                        flexWrap: "wrap",
                        mb: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: { xs: 1.25, sm: 1.5 },
                            minWidth: 0,
                        }}
                    >
                        <Box
                            sx={{
                                p: { xs: 1.1, sm: 1.4 },
                                background: "rgba(102, 126, 234, 0.1)",
                                borderRadius: 2,
                                display: "flex",
                                alignItems: "center",
                                flexShrink: 0,
                            }}
                        >
                            <TrendingUp sx={{ color: "#667eea", fontSize: { xs: 22, sm: 26 } }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: { xs: 16, sm: 18 }, fontWeight: 700, color: "#1E293B", lineHeight: 1.25 }}>
                                Workstation Occupancy
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: "#64748B", mt: 0.5 }}>
                                Hourly workstation occupancy overview
                            </Typography>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                            minWidth: 0,
                        }}
                    >
                        <FormControl
                            size="small"
                            sx={{ minWidth: { xs: 145, sm: 175 } }}
                        >
                            <Select
                                value={selectedWorkspace}
                                onChange={(event) => {
                                    setSelectedWorkspace(event.target.value);
                                    setSelectedPoint(null);
                                }}
                                inputProps={{
                                    "aria-label": "Filter chart by workspace",
                                }}
                            >
                                <MenuItem value="all">All Workspaces</MenuItem>
                                {workspaces.map((workspace) => (
                                    <MenuItem
                                        key={workspace.id}
                                        value={workspace.id}
                                    >
                                        {workspace.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    
                    </Box>
                </Box>
                {error && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {loading ? (
                    <Box
                        sx={{
                            height: { xs: 260, sm: 290, md: 320 },
                            display: "grid",
                            placeItems: "center",
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Box
                            sx={{
                                display: "flex",
                                gap: 3,
                                flexWrap: "wrap",
                                mb: 2,
                            }}
                        >
                            
                           
                               
                            </Box>
                        
                        <Box
                            sx={{
                                width: "100%",
                                minWidth: 0,
                                height: { xs: 260, sm: 290, md: 320 },
                            }}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                    data={chartData}
                                    margin={{
                                        top: 8,
                                        right: 12,
                                        bottom: 8,
                                        left: 8,
                                    }}
                                >
                                    <CartesianGrid
                                        stroke="#e7edf5"
                                        strokeDasharray="3 3"
                                    />
                                    <XAxis
                                        dataKey="time"
                                        tick={{ fontSize: 11, fill: "#64748B" }}
                                        interval="preserveStartEnd"
                                        minTickGap={18}
                                    />
                                    <YAxis
                                        allowDecimals={false}
                                        domain={[
                                            0,
                                            (max) => Math.max(5, max, totalWorkstations),
                                        ]}
                                        width={42}
                                        tick={{ fontSize: 11, fill: "#64748B" }}
                                        label={{
                                            value: "Occupied",
                                            angle: -90,
                                            position: "insideLeft",
                                            style: { textAnchor: "middle" },
                                        }}
                                    />
                                    <ChartTooltip
                                        labelFormatter={(label) => `Hour: ${label}`}
                                        formatter={(value) => [
                                            value,
                                            "Occupied Workstations",
                                        ]}
                                    />
                                    <Line
                                        type="Linear"
                                        dataKey="occupied"
                                        name={selectedName}
                                        stroke="#40518A"
                                        strokeWidth={3}
                                        dot={(props) => {
                                            const { cx, cy, payload } = props;
                                            const isSelected = selectedPoint?.hour === payload.hour;
                                            return (
                                                <circle
                                                    key={`point-${payload.hour}`}
                                                    cx={cx}
                                                    cy={cy}
                                                    r={isSelected ? 6 : 3}
                                                    fill="#40518A"
                                                    stroke={isSelected ? "#FFFFFF" : "none"}
                                                    strokeWidth={2}
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => setSelectedPoint(payload)}
                                                />
                                            );
                                        }}
                                        activeDot={{ r: 6, fill: "#40518A", stroke: "#fff", strokeWidth: 2 }}
                                        isAnimationActive={false}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>
                        {selectedPoint && (
                            <Box
                                sx={{
                                    mt: 2,
                                    p: 2,
                                    borderRadius: 2,
                                    backgroundColor: "#F1F5F9",
                                }}
                            >
                                <Typography variant="subtitle2">
                                    Selected timestamp: {selectedPoint.time}
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{ fontWeight: 700, color: "#40518A" }}
                                >
                                    {selectedPoint.occupied} occupied workstations
                                </Typography>
                            </Box>
                        )}
                        
                    </>
                )}
            </CardContent>
        </Card>
    );
}
